import type {
  CrossSellingRule,
  PolicyFieldDescriptor,
  PolicyFieldValue,
  PolicySectionKey,
  PolicyValueSource,
  EffectivePolicyField,
  TenantCommercialPolicy,
} from '@/lib/api/botSettings'

type SectionValues = Record<string, unknown>

/** Lo que un campo de la sección puede guardar: un valor simple o las reglas de venta cruzada. */
export type SectionValue = PolicyFieldValue | CrossSellingRule[] | null

export function readSection(policy: TenantCommercialPolicy | null, section: PolicySectionKey): SectionValues {
  return (policy?.[section] ?? {}) as SectionValues
}

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim().length === 0
  if (Array.isArray(value)) return value.length === 0
  return false
}

/** Escribe un campo de la sección del vertical sin tocar el resto de la política. */
export function writeSectionValue(
  policy: TenantCommercialPolicy | null,
  section: PolicySectionKey,
  key: string,
  value: SectionValue
): TenantCommercialPolicy {
  const base: TenantCommercialPolicy = policy ?? { schemaVersion: 1 }
  return {
    ...base,
    schemaVersion: 1,
    [section]: { ...readSection(policy, section), [key]: isEmpty(value) ? null : value },
  }
}

/** Valor que el tenant tiene en su borrador para un campo (null si no escribió nada). */
export function readTenantValue(
  policy: TenantCommercialPolicy | null,
  section: PolicySectionKey,
  descriptor: PolicyFieldDescriptor
): PolicyFieldValue | null {
  const raw = readSection(policy, section)[descriptor.key]
  if (isEmpty(raw)) return null
  switch (descriptor.type) {
    case 'number':
      return typeof raw === 'number' ? raw : null
    case 'boolean':
      return typeof raw === 'boolean' ? raw : null
    case 'tags':
      return Array.isArray(raw) ? raw.filter((item): item is string => typeof item === 'string') : null
    default:
      return typeof raw === 'string' ? raw : null
  }
}

/**
 * Origen del valor según el borrador actual: lo que el tenant acaba de escribir
 * manda sobre lo que el servidor devolvió en la última carga.
 */
export function resolveDraftSource(
  tenantValue: PolicyFieldValue | null,
  effective: EffectivePolicyField | undefined
): PolicyValueSource {
  if (tenantValue !== null) return 'tenant'
  return effective?.platformValue !== null && effective?.platformValue !== undefined ? 'platform' : 'unset'
}

export const SOURCE_LABELS: Record<PolicyValueSource, string> = {
  tenant: 'Configurado por ti',
  platform: 'Valor sugerido por la plataforma',
  unset: 'Sin configurar: el asistente lo confirma con el equipo',
}

export function formatPlatformValue(value: PolicyFieldValue | null | undefined): string {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'boolean') return value ? 'Sí' : 'No'
  return String(value)
}
